import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { StatusPill } from "../../components/StatusPill";
import { Button } from "../../components/ui/button";

const RATINGS = ["None", "Mild", "Moderate", "Severe"];

export default function ReportView() {
  const { caseId } = useParams();
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get(`/cases/${caseId}/report`).then((r) => setReport(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [caseId]);

  if (!report) return <DashboardShell title="Your report"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;

  if (report.status !== "approved") {
    return (
      <DashboardShell title="Your report" subtitle="Your clinician and our QA team are finalizing your documentation.">
        <div className="card-warm p-10 max-w-xl text-center" data-testid="report-pending">
          <FileText className="h-10 w-10 mx-auto text-[#7E9B82]" />
          <p className="font-heading text-2xl font-semibold mt-4">Report not yet available</p>
          <div className="mt-4"><StatusPill status={report.case_status || "report_in_progress"} /></div>
          <p className="text-sm text-[#5C5C57] mt-4">You'll be able to view and download your signed PDF here once QA review is complete.</p>
        </div>
      </DashboardShell>
    );
  }

  const labels = report.section_labels || {};
  const matrixIndex = 6;
  const sectionEntries = Object.entries(labels);

  return (
    <DashboardShell title="Your evaluation report" subtitle={`${report.product?.name || ""} · Signed ${report.attestation?.signed_at ? new Date(report.attestation.signed_at).toLocaleDateString() : ""}`}
      actions={<a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/report/pdf`} data-testid="download-pdf-link">
        <Button data-testid="download-pdf-btn" className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
          <Download className="h-4 w-4 mr-2" /> Download PDF
        </Button>
      </a>}>
      <div className="max-w-3xl space-y-8" data-testid="report-content">
        {sectionEntries.slice(0, matrixIndex).map(([k, label]) => (
          <section key={k}>
            <h2 className="font-heading text-2xl font-semibold tracking-tight mb-3">{label}</h2>
            <p className="text-sm leading-7 text-[#1C1C19] whitespace-pre-wrap">{report.sections?.[k]}</p>
          </section>
        ))}
        <section>
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-3">IX. Functional Assessment</h2>
          <div className="card-warm overflow-hidden">
            {(report.functional_matrix || []).map((m) => (
              <div key={m.domain} className="grid grid-cols-3 gap-4 px-5 py-3 border-b border-[#E5E5DF] last:border-0 text-sm" data-testid={`matrix-${m.domain.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <span className="font-semibold">{m.domain}</span>
                <span className={m.rating >= 2 ? "text-[#8C2A2A] font-semibold" : "text-[#5C5C57]"}>{m.rating === null ? "—" : RATINGS[m.rating]}</span>
                <span className="text-[#5C5C57] col-span-3 sm:col-span-1">{m.evidence}</span>
              </div>
            ))}
          </div>
        </section>
        {sectionEntries.slice(matrixIndex).map(([k, label]) => (
          <section key={k}>
            <h2 className="font-heading text-2xl font-semibold tracking-tight mb-3">{label}</h2>
            <p className="text-sm leading-7 whitespace-pre-wrap">{report.sections?.[k]}</p>
          </section>
        ))}
        <section className="card-warm p-6">
          <h2 className="font-heading text-xl font-semibold mb-2">XV. Clinician Attestation</h2>
          <p className="text-sm">/s/ {report.attestation?.name}{report.attestation?.credentials ? `, ${report.attestation.credentials}` : ""}</p>
          <p className="text-xs text-[#5C5C57] mt-2">This report provides clinical documentation that may be submitted to the requesting organization. The receiving agency, employer, school, or insurer determines what evidence it accepts.</p>
        </section>
      </div>
    </DashboardShell>
  );
}
